import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty, fmtDate, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { ItemCombobox } from "../components/ItemCombobox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Download, X, Send, Save, Edit3, Ban, RotateCcw, Printer, FileDown, PenLine, PiggyBank } from "lucide-react";
import { EvidencePanel } from "../components/EvidencePanel";
import { SignTokenDialog } from "../components/SignTokenDialog";
import { QuotesPanel } from "../components/QuotesPanel";
import { CostCodeHeader, AccountPicker, CostCodeChip } from "../components/CostCode";
import { SavingsReportDialog } from "../components/SavingsReportDialog";
import { DeletedRequisitionsDialog } from "../components/DeletedRequisitionsDialog";
import { SearchSelect } from "../components/SearchSelect";
import { ArrowUpDown, Search, Scissors, History } from "lucide-react";

const PRIORITIES = ["urgent", "normal", "low"];
const STATUSES_FILTER = ["draft", "pending", "approved", "rejected", "cancelled", "converted"];

// Short date dd/mm/yy from ISO (or empty)
const fmtShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    // maybe YYYY-MM-DD only
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1].slice(2)}`;
    return "—";
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
};

const PRIO_CLS = { urgent: "border-red-400 text-red-700 bg-red-50", normal: "border-slate-300 text-slate-700", low: "border-emerald-300 text-emerald-700 bg-emerald-50" };
const PriorityBadge = ({ p, t }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 ${PRIO_CLS[p || "normal"]}`} data-testid="req-priority-badge">
    {t(`rq.prio.${p || "normal"}`)}
  </span>
);

const emptyLine = () => ({ item_number: "", part_number: "", qty: 1, est_cost: 0, equipment_id: "", notes: "", cost_code: { subcuenta: "", tipo: "" } });
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const emptyForm = () => ({
  id: null, department: "", notes: "", category: "monthly", priority: "normal", warehouse: "", needed_by: "",
  assigned_approver_id: "", client_code: "", contract_code: "", frente_code: "",
  lines: [emptyLine()],
});

export default function Requisitions() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [signAction, setSignAction] = useState(null);
  const [savingsOpen, setSavingsOpen] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false); // {type:'approve'|'submit', reqId, force}

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [convSupplier, setConvSupplier] = useState("");

  // Filters
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fPriority, setFPriority] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fWarehouse, setFWarehouse] = useState("all");
  const [fQ, setFQ] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  useEffect(() => { const h = setTimeout(() => setQ(fQ.trim()), 300); return () => clearTimeout(h); }, [fQ]);
  const toggleSort = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === "desc" ? "asc" : "desc" }));
  const SortTh = ({ col, children, className = "" }) => (
    <th className={`cursor-pointer select-none ${className}`} onClick={() => toggleSort(col)} data-testid={`req-sort-${col}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={9} className={sort.by === col ? "text-primary" : "opacity-40"} /></span>
    </th>
  );

  // Convert-to-PO (split) state
  const [convKey, setConvKey] = useState("");
  const [convLines, setConvLines] = useState([]);
  const [converting, setConverting] = useState(false);

  // Reason dialogs
  const [reasonAction, setReasonAction] = useState(null); // {type: 'reject'|'cancel', reqId}
  const [reasonText, setReasonText] = useState("");

  const fileRef = useRef(null);

  const load = useCallback(() => {
    const params = {};
    if (fFrom) params.date_from = fFrom;
    if (fTo) params.date_to = fTo;
    if (fPriority !== "all") params.priority = fPriority;
    if (fStatus !== "all") params.status = fStatus;
    if (fWarehouse !== "all") params.warehouse = fWarehouse;
    if (q) params.q = q;
    params.sort_by = sort.by; params.sort_dir = sort.dir;
    return api.get("/requisitions", { params }).then((r) => {
      setRows(r.data);
      const openId = new URLSearchParams(window.location.search).get("open");
      if (openId) { const d = r.data.find((x) => x.id === openId); if (d) { setDetail((cur) => cur || d); openConvert(d); } }
    }).catch(() => {});
  }, [fFrom, fTo, fPriority, fStatus, fWarehouse, q, sort]);

  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/address-book", { params: { type: "supplier" } }).then((r) => setSuppliers(r.data));
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    api.get("/requisitions/approvers", { params: form.department ? { department: form.department } : {} })
      .then((r) => {
        setApprovers(r.data);
        const deptManagers = form.department ? r.data.filter((a) => a.position && (user?.role === "admin" || a.id !== user?.id)) : [];
        if (deptManagers.length) {
          setForm((f) => deptManagers.some((a) => a.id === f.assigned_approver_id) ? f : { ...f, assigned_approver_id: deptManagers[0].id });
        }
      }).catch(() => {});
  }, [form.department, user]);

  const payload = () => ({
    department: form.department, notes: form.notes,
    category: form.category, priority: form.priority, warehouse: form.warehouse, needed_by: form.needed_by,
    assigned_approver_id: form.assigned_approver_id,
    client_code: form.client_code || "", contract_code: form.contract_code || "", frente_code: form.frente_code || "",
    lines: cleanLines(),
  });

  const setLine = (i, patch) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], ...patch };
    if (patch.item_number) {
      const it = items.find((x) => x.item_number === patch.item_number);
      if (it) lines[i].est_cost = it.standard_cost;
    }
    setForm({ ...form, lines });
  };

  const cleanLines = () => form.lines
    .filter((l) => l.item_number)
    .map((l) => ({ item_number: l.item_number, part_number: l.part_number || "", qty: Number(l.qty), est_cost: Number(l.est_cost),
                    equipment_id: l.equipment_id || "", notes: l.notes || "",
                    cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" } }));

  const openNew = () => { setForm(emptyForm()); setOpen(true); };

  const openEdit = (req) => {
    setForm({
      id: req.id,
      department: req.department || "",
      notes: req.notes || "",
      category: req.category || "monthly",
      priority: req.priority || "normal",
      warehouse: req.warehouse || "",
      needed_by: req.needed_by || "",
      assigned_approver_id: req.assigned_approver_id || "",
      client_code: req.client_code || "", contract_code: req.contract_code || "", frente_code: req.frente_code || "",
      lines: (req.lines || []).map((l) => ({
        item_number: l.item_number, part_number: l.part_number || "", qty: l.qty, est_cost: l.est_cost,
        equipment_id: l.equipment_id || "", notes: l.notes || "",
        cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" },
      })),
    });
    setDetail(null);
    setOpen(true);
  };

  const saveDraft = async () => {
    try {
      if (form.id) {
        await api.put(`/requisitions/${form.id}`, payload());
      } else {
        const { data } = await api.post("/requisitions", payload());
        setForm({ ...form, id: data.id });
        toast.success(t("rq.draftSaved"));
        load();
        return;
      }
      toast.success(t("rq.draftSaved"));
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitForApproval = async (signature_token = "") => {
    if (!form.assigned_approver_id) return toast.error(t("rq.assignApprover"));
    try {
      let reqId = form.id;
      if (!reqId) {
        const { data } = await api.post("/requisitions", payload());
        reqId = data.id;
      } else {
        await api.put(`/requisitions/${form.id}`, payload());
      }
      await api.post(`/requisitions/${reqId}/submit`, {
        assigned_approver_id: form.assigned_approver_id,
        category: form.category, priority: form.priority, needed_by: form.needed_by, signature_token,
      });
      toast.success(t("rq.submitted"));
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doAction = async (id, act, body = {}, params = {}) => {
    try {
      const { data } = await api.post(`/requisitions/${id}/${act}`, body, { params });
      toast.success(t(act === "approve" ? "rq.approved" : act === "reject" ? "rq.rejected"
        : act === "cancel" ? "rq.cancelled" : "rq.converted"));
      setDetail(act === "convert" ? null : data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmReason = async () => {
    if (!reasonAction) return;
    if ((reasonText || "").trim().length < (reasonAction.type === "delete" ? 5 : 3)) {
      return toast.error(reasonAction.type === "delete" ? "Justificación obligatoria (mínimo 5 caracteres)" : t("rq.reasonRequired"));
    }
    if (reasonAction.type === "delete") {
      try {
        await api.delete(`/requisitions/${reasonAction.reqId}`, { data: { reason: reasonText.trim() } });
        toast.success("Requisición eliminada con justificación");
        setDetail(null); load();
      } catch (e) { toast.error(errMsg(e)); }
      setReasonAction(null); setReasonText("");
      return;
    }
    await doAction(reasonAction.reqId, reasonAction.type, { reason: reasonText.trim() });
    setReasonAction(null);
    setReasonText("");
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/requisitions/import-excel", fd);
      if (data.errors?.length) {
        data.errors.slice(0, 5).forEach((er) => toast.warning(er));
      }
      if (!data.lines?.length) return toast.error(t("rq.importErrors"));
      // Merge parsed lines into form
      const parsed = data.lines.map((l) => ({
        item_number: l.item_number, part_number: l.part_number || "", qty: l.qty, est_cost: l.est_cost,
        equipment_id: l.equipment_id || "", notes: l.notes || "",
        cost_code: { subcuenta: l.cost_code?.subcuenta || "", tipo: l.cost_code?.tipo || "" },
      }));
      setItems((prev) => { const known = new Set(prev.map((x) => x.item_number)); return [...prev, ...data.lines.filter((l) => !known.has(l.item_number)).map((l) => ({ item_number: l.item_number, description: l.description, standard_cost: l.est_cost }))]; });
      setForm((prev) => ({ ...prev, lines: parsed.length ? parsed : prev.lines }));
      toast.success(`${data.total_parsed} ${t("common.lines")}`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const exportHistory = async () => {
    const params = new URLSearchParams();
    if (fFrom) params.set("date_from", fFrom);
    if (fTo) params.set("date_to", fTo);
    if (fPriority !== "all") params.set("priority", fPriority);
    if (fStatus !== "all") params.set("status", fStatus);
    const qs = params.toString();
    try {
      await downloadReport(`/requisitions/export${qs ? "?" + qs : ""}`, `requisiciones_${Date.now()}.xlsx`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadTemplate = async (format = "xlsx") => {
    try { await downloadReport(`/requisitions/template?format=${format}`, `plantilla_requisicion.${format}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openConvert = (req) => {
    setConvKey(newKey());
    setConvLines((req.lines || []).map((l, i) => !l.po_number ? i + 1 : null).filter(Boolean));
  };
  const convertSelected = async () => {
    if (!detail || converting) return;
    setConverting(true);
    try {
      const useQuote = detail.selected_quote_id && convSupplier === detail.selected_supplier_id;
      const body = { ...(useQuote ? { quote_id: detail.selected_quote_id } : { supplier_id: convSupplier }),
        line_nos: convLines, idempotency_key: convKey };
      const { data } = await api.post(`/requisitions/${detail.id}/convert`, body);
      toast.success(`${t("rq.converted")} · ${data.number}`);
      setDetail(null); load();
    } catch (e) { toast.error(errMsg(e)); setConvKey(newKey()); }
    finally { setConverting(false); }
  };

  const canEditDraft = (r) => r.status === "draft" &&
    (r.requester_email === user?.email || user?.role === "admin");
  const canCancel = (r) => (r.status === "draft" || r.status === "pending") &&
    (r.requester_email === user?.email || user?.role === "admin" || user?.role === "approver");
  const canDelete = (r) => !["converted"].includes(r.status) && !(r.po_ids?.length) &&
    (user?.role === "admin" || user?.role === "approver" || (user?.role === "buyer" && r.status !== "approved"));

  return (
    <div data-testid="requisitions-page">
      <PageHeader title={t("rq.title")} subtitle={t("rq.subtitle")}>
        {can("admin", "buyer", "approver") && (
          <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => setSavingsOpen(true)} data-testid="req-savings-button">
            <PiggyBank size={12} className="mr-1" /> {t("rq.savings")}
          </Button>
        )}
        <SavingsReportDialog open={savingsOpen} onOpenChange={setSavingsOpen} />
        {can("admin", "approver") && (
          <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={() => setDeletedOpen(true)} data-testid="req-deleted-history-button">
            <History size={12} className="mr-1" /> Eliminadas
          </Button>
        )}
        <DeletedRequisitionsDialog open={deletedOpen} onOpenChange={setDeletedOpen} onRestored={load} />
        <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
          onClick={exportHistory} data-testid="req-export-button">
          <Download size={12} className="mr-1" /> {t("rq.export")}
        </Button>
        {can("admin", "buyer", "warehouse", "approver") && (
          <Button size="sm" className="rounded-none text-xs font-bold" onClick={openNew} data-testid="req-new-button">
            <Plus size={13} className="mr-1" /> {t("rq.new")}
          </Button>
        )}
      </PageHeader>

      {/* Filters */}
      <div className="border bg-card p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="req-filters">
        <div className="space-y-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.filterFrom")}</Label>
          <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
            className="rounded-none h-8 text-xs w-36" data-testid="req-filter-from" />
        </div>
        <div className="space-y-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.filterTo")}</Label>
          <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
            className="rounded-none h-8 text-xs w-36" data-testid="req-filter-to" />
        </div>
        <div className="space-y-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.priority")}</Label>
          <Select value={fPriority} onValueChange={setFPriority}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="req-filter-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("rq.filterAll")}</SelectItem>
              {PRIORITIES.map((c) => <SelectItem key={c} value={c}>{t(`rq.prio.${c}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.filterStatus")}</Label>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="req-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("rq.filterAll")}</SelectItem>
              {STATUSES_FILTER.map((s) => <SelectItem key={s} value={s}>{t(`st.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-0.5">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.warehouse")}</Label>
          <Select value={fWarehouse} onValueChange={setFWarehouse}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="req-filter-warehouse"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("rq.filterAll")}</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-0.5 flex-1 min-w-56">
          <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.search")}</Label>
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={fQ} onChange={(e) => setFQ(e.target.value)} placeholder="Folio, solicitante, artículo, No. de parte, equipo, OC…"
              className="rounded-none h-8 text-xs pl-6" data-testid="req-search-input" />
          </div>
        </div>
        {(fFrom || fTo || fPriority !== "all" || fStatus !== "all" || fWarehouse !== "all" || fQ) && (
          <Button size="sm" variant="ghost" className="rounded-none text-xs h-8"
            onClick={() => { setFFrom(""); setFTo(""); setFPriority("all"); setFStatus("all"); setFWarehouse("all"); setFQ(""); }}
            data-testid="req-filter-clear">
            <X size={12} className="mr-1" /> {t("common.clear") || "Limpiar"}
          </Button>
        )}
      </div>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="req-table">
          <table className="tbl">
            <thead><tr>
              <SortTh col="number">{t("common.number")}</SortTh><SortTh col="status">{t("common.status")}</SortTh>
              <SortTh col="priority">{t("rq.priority")}</SortTh><SortTh col="requester_email">{t("rq.requester")}</SortTh>
              <SortTh col="department">{t("rq.department")}</SortTh>
              <SortTh col="warehouse">{t("rq.warehouse")}</SortTh>
              <SortTh col="assigned_approver_email">{t("rq.approver")}</SortTh><SortTh col="needed_by">{t("rq.neededBy")}</SortTh>
              <th className="num">{t("common.lines")}</th>
              <SortTh col="total" className="num">{t("common.total")}</SortTh>
              <SortTh col="created_at">{t("rq.createdDate")}</SortTh>
              <SortTh col="approved_at">{t("rq.approvedDate")}</SortTh>
              <th>{t("rq.linkedPo")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => { setDetail(r); setConvSupplier(""); openConvert(r); }}
                    data-testid={`req-row-${r.number}`}>
                  <td className="font-bold">{r.number}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="text-[11px]"><PriorityBadge p={r.priority} t={t} /></td>
                  <td className="text-[11px]">{r.requester_email}</td>
                  <td className="text-[11px]">{r.department || "—"}</td>
                  <td className="text-[11px] font-mono" data-testid={`req-warehouse-${r.number}`}>{r.warehouse || "—"}</td>
                  <td className="text-[11px] text-muted-foreground">{r.assigned_approver_email || "—"}</td>
                  <td className="text-[11px] tabular-nums">{fmtShort(r.needed_by)}</td>
                  <td className="num">{r.lines.length}</td>
                  <td className="num font-bold">{fmtMoney(r.total)}</td>
                  <td className="text-muted-foreground tabular-nums text-[11px]">{fmtShort(r.created_at)}</td>
                  <td className="text-muted-foreground tabular-nums text-[11px]" data-testid={`req-approved-${r.number}`}>{fmtShort(r.approved_at)}</td>
                  <td className="text-[11px]" data-testid={`req-linked-po-${r.number}`}>
                    {r.wo_number && (
                      <div className="mb-0.5">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/mnt/work-orders/${r.wo_id}`); }}
                          className="inline-flex items-center gap-1 text-[9px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0.5 hover:bg-emerald-200"
                          data-testid={`req-wo-chip-${r.number}`}>
                          OT: {r.wo_number}
                        </button>
                      </div>
                    )}
                    {r.linked_pos?.length ? r.linked_pos.map((p) => (
                      <span key={p.po_id} className="flex items-center gap-1">
                        <span className="font-bold text-primary">{p.po_number}</span>
                        <StatusBadge status={p.po_status} />
                      </span>
                    )) : (r.wo_number ? "" : "—")}
                    {r.partially_converted && <span className="text-[9px] font-bold uppercase text-amber-700" data-testid={`req-partial-${r.number}`}>Parcial</span>}
                  </td>
                  <td><span className="text-[10px] font-bold text-primary" data-testid={`req-view-${r.number}`}>{t("common.view")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">
              {form.id ? t("rq.edit") : t("rq.new")}
            </DialogTitle>
            <DialogDescription className="text-xs">{t("rq.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="req-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((c) => <SelectItem key={c} value={c}>{t(`rq.prio.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.warehouse")}</Label>
              <Select value={form.warehouse || "_none"} onValueChange={(v) => setForm({ ...form, warehouse: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="req-form-warehouse"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.neededBy")}</Label>
              <Input type="date" value={form.needed_by} onChange={(e) => setForm({ ...form, needed_by: e.target.value })}
                className="rounded-none h-8 text-xs" data-testid="req-form-needed-by" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.department")}</Label>
              <Select value={form.department || "_none"}
                      onValueChange={(v) => setForm({ ...form, department: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="req-form-department">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {departments.filter((d) => d.active !== false).map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.assignApprover")}</Label>
              <Select value={form.assigned_approver_id} onValueChange={(v) => setForm({ ...form, assigned_approver_id: v })}>
                <SelectTrigger className="rounded-none h-8 text-xs" data-testid="req-form-approver">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {approvers.filter((a) => user?.role === "admin" || a.id !== user?.id).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.position ? ` · ${a.position}` : ` (${a.role})`} — {a.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.department && approvers.length > 0 && !approvers.some((a) => a.position) && (
                <p className="text-[9px] text-muted-foreground" data-testid="req-approvers-fallback-hint">{t("rq.noApproversDept")}</p>
              )}
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.notes")}</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-none h-8 text-xs" data-testid="req-form-notes" />
            </div>
            <div className="col-span-2">
              <CostCodeHeader value={form} onChange={(patch) => setForm({ ...form, ...patch })} testid="req-cc" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.lines")}</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-none text-[11px] h-7"
                  onClick={() => downloadTemplate("xlsx")} data-testid="req-download-template">
                  <Download size={11} className="mr-1" /> {t("rq.excelTemplate")} (.xlsx)
                </Button>
                <Button variant="ghost" size="sm" className="rounded-none text-[11px] h-7"
                  onClick={() => downloadTemplate("csv")} data-testid="req-download-template-csv">.csv</Button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={onFilePick} data-testid="req-import-input" />
                <Button variant="outline" size="sm" className="rounded-none text-[11px] h-7"
                  onClick={() => fileRef.current?.click()} data-testid="req-import-button">
                  <Upload size={11} className="mr-1" /> {t("rq.importExcel")}
                </Button>
              </div>
            </div>
            {form.lines.map((l, i) => (
              <div key={i} className="border p-2 space-y-1.5" data-testid={`req-line-${i}`}>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 min-w-0">
                    <ItemCombobox value={l.item_number}
                      onChange={(item_number, item_obj) => setLine(i, { item_number, est_cost: item_obj?.standard_cost ?? l.est_cost, part_number: item_obj?.part_number || l.part_number || "" })}
                      placeholder={t("rq.searchItem")}
                      testid={`req-line-combobox-${i}`} />
                  </div>
                  <Input value={l.part_number || ""} onChange={(e) => setLine(i, { part_number: e.target.value })}
                    className="rounded-none h-8 text-xs w-32 font-mono" data-testid={`req-line-part-${i}`} placeholder="No. parte (opc.)" title="Número de parte del fabricante (opcional). Si el artículo no tiene uno, se guarda en el catálogo." />
                  <Input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })}
                    className="rounded-none h-8 text-xs w-20 tabular-nums" data-testid={`req-line-qty-${i}`} placeholder="Cant." />
                  <Input type="number" value={l.est_cost} onChange={(e) => setLine(i, { est_cost: e.target.value })}
                    className="rounded-none h-8 text-xs w-28 tabular-nums" data-testid={`req-line-cost-${i}`} placeholder="Costo" />
                  <button onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
                    className="text-muted-foreground hover:text-destructive" data-testid={`req-line-remove-${i}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
                {l.item_number && (
                  <div className="text-[11px] text-muted-foreground pl-1 truncate" data-testid={`req-line-desc-${i}`}>
                    {items.find((x) => x.item_number === l.item_number)?.description || ""}
                  </div>
                )}
                <div className="flex gap-2 items-center pl-1">
                  <SearchSelect value={l.equipment_id || ""} className="flex-1" testid={`req-line-equipment-${i}`}
                    placeholder={t("rq.noEquipment")} emptyLabel={t("rq.noEquipment")}
                    options={equipment.map((eq) => ({ value: eq.id, label: `${eq.code} — ${eq.name}`, hint: eq.model || eq.area || "" }))}
                    onChange={(v) => setLine(i, { equipment_id: v || "" })} />
                  <Input value={l.notes || ""} onChange={(e) => setLine(i, { notes: e.target.value })}
                    className="rounded-none h-7 text-[11px] flex-1" placeholder={t("rq.lineNotes")}
                    data-testid={`req-line-notes-${i}`} />
                </div>
                <div className="pl-1">
                  <AccountPicker value={l.cost_code} onChange={(patch) => setLine(i, { cost_code: { ...(l.cost_code || {}), ...patch } })}
                    header={form} deptoCode={departments.find((d) => d.name === form.department || d.code === form.department)?.acc_code}
                    equipmentCode={equipment.find((eq) => eq.id === l.equipment_id)?.code || ""} testid={`req-line-cc-${i}`} />
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="req-add-line-button"
              onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}>
              <Plus size={12} className="mr-1" /> {t("common.addLine")}
            </Button>
          </div>

          {form.id ? (
            <EvidencePanel resourceType="req" basePath={`/requisitions/${form.id}/evidence`} canWrite />
          ) : (
            <p className="text-[10px] text-muted-foreground border-t pt-2" data-testid="req-attachments-hint">{t("rq.attachmentsDraftHint")}</p>
          )}
          <DialogFooter className="gap-2 flex-wrap sm:justify-end">
            <Button variant="outline" onClick={saveDraft} disabled={!form.lines.some((l) => l.item_number)}
              className="rounded-none font-bold text-xs" data-testid="req-save-draft-button">
              <Save size={12} className="mr-1" /> {t("rq.saveDraft")}
            </Button>
            <Button onClick={() => setSignAction({ type: "submit" })}
              disabled={!form.lines.some((l) => l.item_number) || !form.assigned_approver_id}
              className="rounded-none font-bold text-xs" data-testid="req-submit-button">
              <Send size={12} className="mr-1" /> {t("rq.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black flex items-center gap-3">
                  {detail.number} <StatusBadge status={detail.status} />
                  <PriorityBadge p={detail.priority} t={t} />
                </DialogTitle>
                <DialogDescription className="text-xs">{t("rq.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="text-xs space-y-1">
                <div><span className="font-bold">{t("rq.requester")}:</span> {detail.requester_email}
                  {detail.requester_signature_at && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5" data-testid="req-requester-signature">
                      <PenLine size={10} /> {t("rq.signedDigitally")} · {fmtDate(detail.requester_signature_at)}
                    </span>
                  )}
                </div>
                <div><span className="font-bold">{t("rq.department")}:</span> {detail.department || "—"}</div>
                <div><span className="font-bold">{t("rq.warehouse")}:</span> {detail.warehouse || "—"}</div>
                <div><span className="font-bold">{t("rq.neededBy")}:</span> {detail.needed_by || "—"}</div>
                <div><span className="font-bold">{t("rq.assignApprover")}:</span> {detail.assigned_approver_email || "—"}</div>
                {detail.approver_email && ["approved", "converted"].includes(detail.status) && (
                  <div><span className="font-bold">{t("rq.approver")}:</span> {detail.approver_email}
                    {detail.digital_signature && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5" data-testid="req-digital-signature">
                        <PenLine size={10} /> {t("rq.signedDigitally")} · {fmtDate(detail.digital_signature.at)}
                      </span>
                    )}
                  </div>
                )}
                {detail.po_number && <div><span className="font-bold">OC:</span> {detail.po_number}</div>}
                {detail.wo_number && (
                  <div data-testid="req-detail-linked-wo">
                    <span className="font-bold">OT origen:</span>{" "}
                    <button onClick={() => navigate(`/mnt/work-orders/${detail.wo_id}`)}
                      className="text-emerald-700 font-bold hover:underline">
                      {detail.wo_number}
                    </button>
                    {detail.equipment_code && <span className="text-muted-foreground"> · {detail.equipment_code}</span>}
                  </div>
                )}
                {detail.notes && <div><span className="font-bold">{t("common.notes")}:</span> {detail.notes}</div>}
                {detail.last_rejection_reason && detail.status === "draft" && (
                  <div className="border border-destructive/40 bg-destructive/5 p-2 mt-2" data-testid="req-rejection-reason">
                    <div className="font-bold text-destructive">{t("rq.lastRejection")}:</div>
                    <div>{detail.last_rejection_reason}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      — {detail.last_rejected_by} · {fmtDate(detail.last_rejected_at)}
                    </div>
                  </div>
                )}
                {detail.cancellation_reason && (
                  <div className="border border-destructive/40 bg-destructive/5 p-2 mt-2">
                    <div className="font-bold text-destructive">{t("rq.reason")}:</div>
                    <div>{detail.cancellation_reason}</div>
                  </div>
                )}
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr><th>{t("common.item")}</th><th>No. parte</th><th>{t("common.description")}</th><th>{t("eq.equipmentFor")}</th><th className="num">{t("common.qty")}</th><th>{t("common.uom")}</th><th className="num">{t("rq.estCost")}</th><th>OC</th></tr></thead>
                  <tbody>
                    {detail.lines.map((l, i) => (
                      <tr key={i} className={l.po_number ? "opacity-70" : ""}><td className="font-bold tabular-nums">{l.item_number}</td><td className="font-mono text-[11px]">{l.part_number || "—"}</td><td>{l.description}{l.notes && <div className="text-[10px] text-muted-foreground italic">{l.notes}</div>}<div><CostCodeChip cc={l.cost_code} testid={`req-detail-cc-${i}`} /></div></td>
                        <td className="text-[11px]">{l.equipment_code ? <><span className="font-bold text-primary">{l.equipment_code}</span> <span className="text-muted-foreground">— {l.equipment_name}</span></> : "—"}</td>
                        <td className="num">{fmtQty(l.qty)}</td><td>{l.uom}</td><td className="num">{fmtMoney(l.est_cost)}</td>
                        <td className="text-[11px] font-bold text-primary" data-testid={`req-detail-line-po-${i}`}>{l.po_number || <span className="text-muted-foreground font-normal">pendiente</span>}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.linked_po && (
                <div className="border border-primary/30 bg-primary/5 p-3" data-testid="req-detail-linked-po">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">{t("rq.linkedPo")}</div>
                  <div className="text-xs flex items-center gap-3 flex-wrap">
                    <span className="font-black text-primary">{detail.linked_po.po_number}</span>
                    <StatusBadge status={detail.linked_po.po_status} />
                    <span>{detail.linked_po.supplier_name}</span>
                    <span className="font-bold tabular-nums">{fmtMoney(detail.linked_po.po_total)}</span>
                    <span className="text-muted-foreground tabular-nums text-[11px]">{fmtShort(detail.linked_po.po_date)}</span>
                  </div>
                </div>
              )}

              {["pending", "approved", "converted"].includes(detail.status) && (
                <QuotesPanel req={detail} suppliers={suppliers} canEdit={can("admin", "buyer")}
                  onSelected={() => api.get("/requisitions").then((r) => { setRows(r.data); const d = r.data.find((x) => x.id === detail.id); if (d) { setDetail(d); if (d.selected_supplier_id) setConvSupplier(d.selected_supplier_id); } })} />
              )}
              <EvidencePanel resourceType="req" basePath={`/requisitions/${detail.id}/evidence`}
                canWrite={can("admin", "buyer", "warehouse", "approver", "maintenance")} hideDelete={!can("admin", "buyer", "approver")} />

              {detail.history?.length > 0 && (
                <details className="text-[11px] border p-2">
                  <summary className="font-bold uppercase tracking-widest cursor-pointer text-muted-foreground">
                    {t("rq.history")} ({detail.history.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {detail.history.map((h, i) => (
                      <li key={i} className="flex gap-2 text-muted-foreground">
                        <span className="font-bold uppercase">{h.action}</span>
                        <span>{h.by}</span>
                        <span>{fmtDate(h.at)}</span>
                        {h.reason && <span className="text-destructive">— {h.reason}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="flex gap-2 flex-wrap items-center">
                <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/requisitions/${detail.id}/pdf`, "_blank")}
                  data-testid="req-download-pdf">
                  <FileDown size={12} className="mr-1" /> {t("rq.downloadPdf")}
                </Button>
                <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                  onClick={() => {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/requisitions/${detail.id}/pdf`;
                    const w = window.open(url, "_blank");
                    if (w) {
                      w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) {} });
                    }
                  }}
                  data-testid="req-print-button">
                  <Printer size={12} className="mr-1" /> {t("rq.print")}
                </Button>
                {canEditDraft(detail) && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                    onClick={() => openEdit(detail)} data-testid="req-edit-button">
                    <Edit3 size={12} className="mr-1" /> {t("rq.edit")}
                  </Button>
                )}
                {detail.status === "pending" && can("admin", "approver") && (
                  <>
                    <Button size="sm" className="rounded-none text-xs font-bold" onClick={() => setSignAction({ type: "approve", reqId: detail.id })}
                      data-testid="req-approve-button">{t("common.approve")}</Button>
                    {user?.role === "admin" && (
                      <Button size="sm" variant="outline" className="rounded-none text-xs font-bold border-amber-500 text-amber-700 dark:text-amber-400"
                        onClick={() => setSignAction({ type: "approve", reqId: detail.id, force: true })}
                        data-testid="req-force-approve-button">{t("rq.forceApprove")}</Button>
                    )}
                    <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                      onClick={() => { setReasonAction({ type: "reject", reqId: detail.id }); setReasonText(""); }}
                      data-testid="req-reject-button">
                      <RotateCcw size={12} className="mr-1" /> {t("rq.reject")}
                    </Button>
                  </>
                )}
                {canCancel(detail) && (
                  <Button size="sm" variant="destructive" className="rounded-none text-xs font-bold"
                    onClick={() => { setReasonAction({ type: "cancel", reqId: detail.id }); setReasonText(""); }}
                    data-testid="req-cancel-button">
                    <Ban size={12} className="mr-1" /> {t("rq.cancel")}
                  </Button>
                )}
                {canDelete(detail) && (
                  <Button size="sm" variant="outline" className="rounded-none text-xs font-bold border-destructive text-destructive"
                    onClick={() => { setReasonAction({ type: "delete", reqId: detail.id }); setReasonText(""); }}
                    data-testid="req-delete-button">
                    <Trash2 size={12} className="mr-1" /> {t("common.delete")}
                  </Button>
                )}
                {detail.status === "approved" && can("admin", "buyer") && (
                  <>
                    <Select value={convSupplier} onValueChange={setConvSupplier}>
                      <SelectTrigger className="rounded-none h-8 text-xs w-64" data-testid="req-convert-supplier-select">
                        <SelectValue placeholder={t("common.supplier")} />
                      </SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" className="rounded-none text-xs font-bold" disabled={!convSupplier || converting || convLines.length === 0}
                      onClick={convertSelected}
                      data-testid="req-convert-button">{converting ? "Generando…" : t("rq.convert")}{convLines.length && convLines.length < detail.lines.filter((l) => !l.po_number).length ? ` (${convLines.length} partidas)` : ""}{detail.selected_quote_id && convSupplier === detail.selected_supplier_id ? ` · ${t("qt.withWinner")}` : ""}</Button>
                  </>
                )}
              </div>
              {detail.status === "approved" && can("admin", "buyer") && (
                <div className="border border-dashed p-2 text-[11px] space-y-1" data-testid="req-split-panel">
                  <div className="font-bold uppercase tracking-widest text-[10px] text-muted-foreground flex items-center gap-1"><Scissors size={10} /> Partidas a incluir en esta OC (puedes dividir en varias OC)</div>
                  <div className="flex flex-wrap gap-2">
                    {detail.lines.map((l, i) => {
                      const n = i + 1; const locked = !!l.po_number;
                      return (
                        <label key={i} className={`inline-flex items-center gap-1 border px-2 py-1 ${locked ? "opacity-50 line-through" : "cursor-pointer"}`} data-testid={`req-split-line-${i}`}>
                          <input type="checkbox" disabled={locked} checked={!locked && convLines.includes(n)}
                            onChange={(e) => setConvLines((cur) => e.target.checked ? [...cur, n].sort((a, b) => a - b) : cur.filter((x) => x !== n))} />
                          <span>{n}. {l.item_number} × {fmtQty(l.qty)}</span>
                          {locked && <span className="text-[9px] font-bold text-primary no-underline">{l.po_number}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <SignTokenDialog open={!!signAction} onOpenChange={(o) => !o && setSignAction(null)} testid="req"
        title={signAction?.type === "submit" ? t("rq.submit") : t("common.approve")}
        skipLabel={signAction?.type === "submit" ? t("rq.submit") : t("common.approve")}
        confirmLabel={signAction?.type === "submit" ? `${t("rq.submit")} + ${t("rq.signWithToken")}` : t("rq.approveAndSign")}
        onConfirm={(token) => {
          if (signAction?.type === "submit") submitForApproval(token);
          else doAction(signAction.reqId, "approve", { signature_token: token }, signAction.force ? { force: true } : {});
          setSignAction(null);
        }} />

      {/* REASON DIALOG */}
      <Dialog open={!!reasonAction} onOpenChange={(o) => { if (!o) { setReasonAction(null); setReasonText(""); } }}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">
              {reasonAction?.type === "cancel" ? t("rq.cancel") : reasonAction?.type === "delete" ? `${t("common.delete")} requisición` : t("rq.reject")}
            </DialogTitle>
            <DialogDescription className="text-xs">{reasonAction?.type === "delete" ? "Justifica por qué se elimina (mínimo 5 caracteres). Se conservará una copia de auditoría." : t("rq.reasonRequired")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("rq.reason")}</Label>
            <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} rows={4}
              className="rounded-none text-xs" data-testid="req-reason-textarea" />
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={confirmReason} className="rounded-none font-bold text-xs"
              data-testid="req-reason-confirm">
              {reasonAction?.type === "cancel" ? t("rq.cancel") : reasonAction?.type === "delete" ? t("common.delete") : t("rq.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
